import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog } from '@/ui/Dialog';
import { InputSuelto } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import {
  exportar, useBorrarRegistro, useRegistros,
  type Campo, type OrdenDeTabla, type Registro,
} from './api';
import { Correo } from './Correo';
import { FormularioDeRecurso } from './FormularioDeRecurso';
import { FormularioDeRegistro } from './FormularioDeRegistro';
import { Importar } from './Importar';
import { Versiones } from './Versiones';
import { CabeceraDeZona, ValorDeCampo, useBusquedaDemorada } from './piezas';

/**
 * Recurso — la tabla de registros de una base de datos.
 * `#recursos-privados/recurso/<id>`.
 *
 * ## Lo que cambia
 *
 * - **Ordenar por una columna ordena de verdad.** `sortBy` ordenaba el array en
 *   memoria y acto seguido llamaba a `_loadEntries()`, que vuelve a pedir la
 *   tabla al servidor: el orden se perdía antes de pintarse. `list_entries`
 *   acepta `sort` y `order` desde que existe, así que ahora se le piden.
 * - **La cabecera es un botón con `aria-sort`**, no un `<th onclick>`.
 * - **La fila es un botón de verdad**, y las acciones de la derecha ya no
 *   dependen de un `stopPropagation` en el marcado.
 * - **Exportar usa `Utils.downloadAuthed`**, que es lo que existe para eso; el
 *   vanilla armaba un `fetch` con el token a mano tres líneas más arriba.
 * - **Eliminar un registro ya no cierra el formulario abierto.**
 */
export function Recurso({ id }: { id: number }) {
  const [texto, setTexto] = useState('');
  const busqueda = useBusquedaDemorada(texto);
  const [retirados, setRetirados] = useState(false);
  const [orden, setOrden] = useState<OrdenDeTabla | null>(null);

  const consulta = useRegistros(id, busqueda, retirados, orden);
  const borrar = useBorrarRegistro();

  const [editandoColumnas, setEditandoColumnas] = useState(false);
  const [importando, setImportando] = useState(false);
  const [registro, setRegistro] = useState<number | null | undefined>(undefined);
  const [versionesDe, setVersionesDe] = useState<number | null>(null);
  const [correoDe, setCorreoDe] = useState<number | null>(null);
  const [borrandoId, setBorrandoId] = useState<number | null>(null);

  const d = consulta.data;
  const recurso = d?.resource;
  const campos: Campo[] = recurso?.fields ?? [];
  const entradas = d?.entries ?? [];
  const esSistema = !!recurso?.is_system;
  /** Un sub-dominio del expediente: cada fila pertenece a un colaborador. */
  const esExpediente = !!recurso?.is_expediente && !esSistema;
  const ocultos = d?.retirados_ocultos ?? 0;

  function ordenarPor(campo: Campo) {
    const clave = campo.key;
    setOrden((o) => (o?.campo === clave
      ? (o.sentido === 'asc' ? { campo: clave, sentido: 'desc' } : null)
      : { campo: clave, sentido: 'asc' }));
  }

  return (
    <>
      <CabeceraDeZona
        icono={recurso?.icon ?? 'bi-database'}
        titulo={
          <>
            <Icono nombre={recurso?.icon ?? ''} respaldo="bi-database" />
            <span className="truncate">{recurso?.name ?? 'Registros'}</span>
            {esSistema && (
              <Badge tone="info">
                <i className="bi bi-shield-lock" aria-hidden="true" /> Sistema · auto-sincronizado
              </Badge>
            )}
          </>
        }
      >
        <Button variant="secondary" size="sm" onClick={() => void exportar(id, 'xlsx', retirados)}>
          <i className="bi bi-file-earmark-excel" aria-hidden="true" /> Excel
        </Button>
        <Button variant="secondary" size="sm" onClick={() => void exportar(id, 'pdf', retirados)}>
          <i className="bi bi-file-earmark-pdf" aria-hidden="true" /> PDF
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setEditandoColumnas(true)}>
          <i className="bi bi-sliders" aria-hidden="true" /> Columnas
        </Button>
        {/* La Base de Datos Empleados se llena sola desde el maestro: el
            servidor rechaza tanto la importación como el alta a mano. */}
        {!esSistema && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setImportando(true)}>
              <i className="bi bi-upload" aria-hidden="true" /> Importar
            </Button>
            <Button size="sm" onClick={() => setRegistro(null)}>
              <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nuevo registro
            </Button>
          </>
        )}
      </CabeceraDeZona>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <InputSuelto
          type="search"
          aria-label="Buscar registros"
          className="max-w-80"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar…"
        />
        {esSistema && (ocultos > 0 || retirados) && (
          <label
            className="flex cursor-pointer items-center gap-2 text-2 text-ink-2"
            title="Quien sale de la empresa deja de listarse aquí, pero su ficha y su expediente se conservan"
          >
            <input
              type="checkbox"
              checked={retirados}
              onChange={(e) => setRetirados(e.target.checked)}
            />
            <i className="bi bi-person-dash" aria-hidden="true" />
            {retirados ? 'Ocultar retirados' : `Ver ${ocultos} retirado${ocultos === 1 ? '' : 's'}`}
          </label>
        )}
      </div>

      {consulta.isPending ? (
        <SkeletonFilas filas={6} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="los registros"
          onReintentar={() => void consulta.refetch()}
          onVolver={() => { location.hash = '#recursos-privados'; }}
        />
      ) : !entradas.length ? (
        <EstadoVacio
          icono={busqueda ? 'bi-search' : 'bi-table'}
          titulo={busqueda ? 'Sin resultados' : 'Sin registros'}
          descripcion={busqueda
            ? `Nada coincide con «${busqueda}».`
            : 'Las columnas ya están listas — agrega el primer registro.'}
          {...(!busqueda && !esSistema
            ? { accion: { texto: 'Nuevo registro', onClick: () => setRegistro(null) } }
            : {})}
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full border-collapse text-3">
            <thead>
              <tr className="border-b border-line bg-sunken text-left">
                {esExpediente && (
                  <th scope="col" className="whitespace-nowrap px-3 py-2 font-semibold text-brand-ink">
                    Colaborador
                  </th>
                )}
                {campos.map((f) => (
                  <th
                    key={f.key}
                    scope="col"
                    className="px-0 py-0"
                    aria-sort={orden?.campo === f.key
                      ? (orden.sentido === 'asc' ? 'ascending' : 'descending')
                      : 'none'}
                  >
                    <button
                      type="button"
                      className="flex w-full items-center gap-1 whitespace-nowrap px-3 py-2 text-left font-semibold text-brand-ink hover:bg-line/40 focus-visible:focus-anillo"
                      onClick={() => ordenarPor(f)}
                    >
                      {f.label}
                      <i
                        className={cn('bi text-1', orden?.campo === f.key
                          ? (orden.sentido === 'asc' ? 'bi-sort-down-alt' : 'bi-sort-down')
                          : 'bi-arrow-down-up opacity-30')}
                        aria-hidden="true"
                      />
                    </button>
                  </th>
                ))}
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {entradas.map((e) => (
                <Fila
                  key={e.id}
                  entrada={e}
                  campos={campos}
                  esExpediente={esExpediente}
                  esSistema={esSistema}
                  onAbrir={() => setRegistro(e.id)}
                  onVersiones={() => setVersionesDe(e.id)}
                  onCorreo={() => setCorreoDe(e.id)}
                  onBorrar={() => setBorrandoId(e.id)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editandoColumnas && (
        <FormularioDeRecurso id={id} onCerrar={() => setEditandoColumnas(false)} />
      )}
      {importando && recurso && (
        <Importar recurso={recurso} onCerrar={() => setImportando(false)} />
      )}
      {registro !== undefined && (
        <FormularioDeRegistro
          id={registro}
          recursoId={id}
          campos={campos}
          esSistema={esSistema}
          pideColaborador={esExpediente}
          onCerrar={() => setRegistro(undefined)}
        />
      )}
      {versionesDe != null && (
        <Versiones id={versionesDe} onCerrar={() => setVersionesDe(null)} />
      )}
      {correoDe != null && <Correo id={correoDe} onCerrar={() => setCorreoDe(null)} />}

      <ConfirmDialog
        abierto={borrandoId != null}
        onCambio={(v) => { if (!v) setBorrandoId(null); }}
        titulo="Eliminar el registro"
        descripcion="El registro deja de listarse y queda constancia en la auditoría. Su historial de versiones se conserva."
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (borrandoId == null) return;
          borrar.mutate(borrandoId, {
            onSuccess: () => {
              Utils.toast('Registro eliminado', 'success');
              setBorrandoId(null);
              setRegistro(undefined);
            },
            onError: (err) => {
              setBorrandoId(null);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          });
        }}
      />
    </>
  );
}

function Fila({
  entrada, campos, esExpediente, esSistema, onAbrir, onVersiones, onCorreo, onBorrar,
}: {
  entrada: Registro;
  campos: Campo[];
  esExpediente: boolean;
  esSistema: boolean;
  onAbrir: () => void;
  onVersiones: () => void;
  onCorreo: () => void;
  onBorrar: () => void;
}) {
  const datos = entrada.data ?? {};
  // El nombre del registro, para que las acciones digan a cuál se refieren.
  const cual = entrada.subject_name
    || String(datos[campos[0]?.key ?? ''] ?? `registro ${entrada.id}`);

  return (
    <tr className="border-b border-line last:border-0 hover:bg-sunken">
      {esExpediente && (
        <td className="whitespace-nowrap px-3 py-2 align-top font-semibold">
          <span className={entrada.subject_user_id ? 'text-brand-ink' : 'text-danger-ink'}>
            {entrada.subject_name || '— sin colaborador —'}
          </span>
        </td>
      )}
      {campos.map((f, i) => (
        <td key={f.key} className="min-w-28 break-words px-3 py-2 align-top">
          {/* La primera celda abre el registro: una fila entera que es un botón
              no deja seleccionar su texto ni copiar un correo. */}
          {i === 0 && !esExpediente ? (
            <button
              type="button"
              className="text-left font-semibold text-brand-ink hover:underline focus-visible:focus-anillo"
              onClick={onAbrir}
            >
              <ValorDeCampo campo={f} valor={datos[f.key]} />
            </button>
          ) : (
            <ValorDeCampo campo={f} valor={datos[f.key]} />
          )}
        </td>
      ))}
      <td className="whitespace-nowrap px-2 py-1 text-right align-top">
        <span className="mr-1 text-1 text-muted" title={`Versión ${entrada.version}`}>
          v{entrada.version}
        </span>
        {esExpediente && (
          <Button variant="ghost" size="sm" iconOnly aria-label={`Editar ${cual}`} onClick={onAbrir}>
            <i className="bi bi-pencil" aria-hidden="true" />
          </Button>
        )}
        <Button variant="ghost" size="sm" iconOnly aria-label={`Historial de ${cual}`} onClick={onVersiones}>
          <i className="bi bi-clock-history" aria-hidden="true" />
        </Button>
        <Button variant="ghost" size="sm" iconOnly aria-label={`Enviar ${cual} por correo`} onClick={onCorreo}>
          <i className="bi bi-envelope" aria-hidden="true" />
        </Button>
        {/* En la Base de Datos Empleados la fila refleja a un colaborador: se
            inactiva desde Administración → Usuarios, y el servidor lo exige. */}
        {!esSistema && (
          <Button variant="ghost" size="sm" iconOnly aria-label={`Eliminar ${cual}`} onClick={onBorrar}>
            <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
          </Button>
        )}
      </td>
    </tr>
  );
}
